import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Box, Fade } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { gsap } from 'gsap'
import AriaDatePicker from './AriaDatePicker'
import styles from './DateRangeInput.module.scss'

const DateRangeInput = React.memo(function DateRangeInput ({ startDate, endDate, onStartDateChange, onEndDateChange, disabled = false }) {
  const { t } = useTranslation()

  const containerRef = useRef(null)

  const [isLoading, setIsLoading] = useState(false)

  const startDateTimeoutRef = useRef(null)
  const endDateTimeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      if (startDateTimeoutRef.current) clearTimeout(startDateTimeoutRef.current)
      if (endDateTimeoutRef.current) clearTimeout(endDateTimeoutRef.current)
    }
  }, [])

  const formatDateForEvent = (date) => {
    if (!date) return ''

    /* istanbul ignore next -- defensive guard; date picker prevents invalid dates */
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      console.warn('Invalid date passed to formatDateForEvent:', date)
      return ''
    }

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  const handleStartDateChange = (date) => {
    if (onStartDateChange) {
      setIsLoading(true)

      if (startDateTimeoutRef.current) clearTimeout(startDateTimeoutRef.current)

      const event = {
        target: {
          value: formatDateForEvent(date)
        }
      }

      startDateTimeoutRef.current = setTimeout(() => {
        onStartDateChange(event)
        setTimeout(() => setIsLoading(false), 300)
      }, 100)
    }
  }

  const handleEndDateChange = (date) => {
    if (onEndDateChange) {
      setIsLoading(true)

      if (endDateTimeoutRef.current) clearTimeout(endDateTimeoutRef.current)

      const event = {
        target: {
          value: formatDateForEvent(date)
        }
      }

      endDateTimeoutRef.current = setTimeout(() => {
        onEndDateChange(event)
        setTimeout(() => setIsLoading(false), 300)
      }, 100)
    }
  }

  /* istanbul ignore next */
  useEffect(() => {
    if (containerRef.current && gsap) {
      try {
        const hoverAnimation = () => {
          if (typeof gsap.to === 'function') {
            gsap.to(containerRef.current.querySelectorAll('input'), {
              boxShadow: '0 4px 12px rgba(43, 88, 118, 0.15)',
              duration: 0.3
            })
          }
        }

        const leaveAnimation = () => {
          if (typeof gsap.to === 'function') {
            gsap.to(containerRef.current.querySelectorAll('input'), {
              boxShadow: 'none',
              duration: 0.3
            })
          }
        }

        const currentRef = containerRef.current
        currentRef.addEventListener('mouseenter', hoverAnimation)
        currentRef.addEventListener('mouseleave', leaveAnimation)

        return () => {
          if (currentRef) {
            currentRef.removeEventListener('mouseenter', hoverAnimation)
            currentRef.removeEventListener('mouseleave', leaveAnimation)
          }
        }
      } catch (error) {
        console.error('Animation error:', error)
      }
    }
  }, [])

  return (
    <div className={styles.dateRangeContainer} ref={containerRef}>
      {/* Loading overlay with fade transition */}
      <Fade in={isLoading || disabled} timeout={300}>
        <div className={styles.loadingOverlay}>
          <div className={styles.fadeContainer} />
        </div>
      </Fade>

      <Box className={styles.dateRangeForm}>
        <Box className={styles.dateFieldContainer}>
          <AriaDatePicker
            label={t('analytics.dateRange.start')}
            value={startDate instanceof Date ? startDate : null}
            onChange={handleStartDateChange}
            disabled={disabled}
            maxDate={endDate instanceof Date ? endDate : new Date()}
          />
        </Box>

        <Box className={styles.dateConnector}>
          <span className={styles.connectorLabel}>{t('common.to')}</span>
        </Box>

        <Box className={styles.dateFieldContainer}>
          <AriaDatePicker
            label={t('analytics.dateRange.end')}
            value={endDate instanceof Date ? endDate : null}
            onChange={handleEndDateChange}
            disabled={disabled}
            minDate={startDate instanceof Date ? startDate : null}
            maxDate={new Date()}
          />
        </Box>
      </Box>
    </div>
  )
})

DateRangeInput.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onStartDateChange: PropTypes.func.isRequired,
  onEndDateChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default DateRangeInput
